import type { ClientMessage, ServerMessage } from '@shared/protocol'

export type SocketStatus = 'closed' | 'connecting' | 'open'

export interface SignalingClientOptions {
    url: string
    onMessage: (message: ServerMessage) => void
    onStatus: (status: SocketStatus) => void
    /** O servidor deu o lugar a uma conexao mais nova sua. Nao volta sozinho. */
    onReplaced?: () => void
    /** Injetavel pra teste. */
    createSocket?: (url: string) => WebSocket
}

const PING_INTERVAL_MS = 25_000
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000]

/**
 * Codigo de fechamento de quem foi substituido por outra aba.
 *
 * Precisa ser terminal: reconectar aqui faria a aba antiga retomar o lugar e
 * derrubar a nova, que reconectaria e derrubaria a antiga — sem fim.
 */
const CLOSE_REPLACED = 4005

/**
 * WebSocket com reconexao automatica e ping periodico.
 *
 * Reconectar aqui nao recoloca ninguem na sala: quem decide isso e o
 * `useRoomSession`, que reenvia o `join` assim que o socket volta.
 */
export class SignalingClient {
    private readonly options: SignalingClientOptions
    private socket: WebSocket | null = null
    private pingTimer: ReturnType<typeof setInterval> | null = null
    private retryTimer: ReturnType<typeof setTimeout> | null = null
    private attempt = 0
    private closedByUser = false

    constructor(options: SignalingClientOptions) {
        this.options = options
    }

    connect(): void {
        if (this.socket && this.socket.readyState <= WebSocket.OPEN) return
        // O `onclose` do socket substituido sai no guarda de obsoleto ANTES de
        // limpar os timers, entao o ping dele sobreviveria a troca: cada
        // reconexao deixava mais um intervalo de 25s vivo pingando no socket
        // atual, ate a aba fechar. Limpar aqui e o unico ponto que sempre roda.
        this.clearTimers()
        this.closedByUser = false
        this.options.onStatus('connecting')

        const factory = this.options.createSocket ?? ((url: string) => new WebSocket(url))
        const socket = factory(this.options.url)
        this.socket = socket

        /**
         * Evento de um socket que ja foi substituido.
         *
         * Acontece de verdade: `connect()` roda com o anterior ainda em
         * CLOSING (o `onerror` fecha e o `onclose` demora um tique), cria o
         * novo, e ai chega o `onclose` do velho. Sem esta checagem ele zerava
         * `this.socket` — o novo abria, mas todo `send` passava a devolver
         * `false` e o `join` nunca saia: a pessoa ficava presa em "conectando".
         */
        const obsoleto = () => this.socket !== socket

        socket.onopen = () => {
            if (obsoleto()) return
            this.attempt = 0
            this.options.onStatus('open')
            this.pingTimer = setInterval(() => this.send({ t: 'ping' }), PING_INTERVAL_MS)
        }

        socket.onmessage = (event) => {
            if (obsoleto()) return
            try {
                this.options.onMessage(JSON.parse(String(event.data)) as ServerMessage)
            } catch {
                // Mensagem fora do protocolo: ignora em vez de derrubar a sala.
            }
        }

        socket.onclose = (event) => {
            if (obsoleto()) return
            this.clearTimers()
            this.socket = null
            const substituido = event.code === CLOSE_REPLACED
            if (substituido) this.closedByUser = true
            this.options.onStatus('closed')
            if (substituido) return this.options.onReplaced?.()
            if (!this.closedByUser) this.scheduleRetry()
        }

        socket.onerror = () => socket.close()
    }

    send(message: ClientMessage): boolean {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false
        this.socket.send(JSON.stringify(message))
        return true
    }

    close(): void {
        this.closedByUser = true
        this.clearTimers()
        this.socket?.close()
        this.socket = null
    }

    private scheduleRetry() {
        const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] ?? 8000
        this.attempt += 1
        this.retryTimer = setTimeout(() => this.connect(), delay)
    }

    private clearTimers() {
        if (this.pingTimer) clearInterval(this.pingTimer)
        if (this.retryTimer) clearTimeout(this.retryTimer)
        this.pingTimer = null
        this.retryTimer = null
    }
}

/** URL do socket: mesma origem em producao, proxy do Vite em dev. */
export function defaultSignalingUrl(): string {
    const override = import.meta.env?.VITE_SIGNALING_URL
    if (override) return override
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    return `${protocol}://${window.location.host}/ws`
}
